import type { FC, ReactNode } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSelectedView } from '../../useSelectedView.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PillsContainer = styled.div({
	paddingLeft: token('space.300'),
	display: 'flex',
	alignItems: 'center',
	paddingRight: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-button -- To migrate as part of go/ui-styling-standard
const PillButton = styled.button<{
	isSelected?: boolean;
}>({
	outline: 'none',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.isSelected ? token('color.background.selected') : token('color.background.neutral'),
	display: 'inline-flex',
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props.isSelected ? token('color.text.selected') : token('color.text')),
	cursor: 'pointer',
	userSelect: 'none',
	whiteSpace: 'nowrap',
	verticalAlign: 'top',
	marginLeft: token('space.100'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props) =>
			props.isSelected
				? token('color.background.selected.hovered')
				: token('color.background.neutral.hovered'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		marginLeft: 0,
	},

	padding: token('space.100'),
	// TODO: Change B400 here to B700 when it becomes available
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) =>
		props.isSelected
			? `${token('border.width')} solid ${token('color.border.focused')}`
			: `${token('border.width')} solid ${token('color.background.neutral')}`,
	borderRadius: token('radius.full'),
	height: '32px',
	justifyContent: 'center',
	alignItems: 'center',
	minWidth: '48px',
	'&:focus': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		borderColor: 'transparent',
	},
});

export type PillFilter = {
	i18n: ReactNode;
	name: string;
};

export type PillProps = {
	pill?: string;
	setPill?: (pill: string) => void;
	pills?: PillFilter[];
};

export const Pills: FC<PillProps> = ({ pill, setPill, pills }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const selectedView = useSelectedView();

	const onClick = (pillButton: PillFilter) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${selectedView}Screen`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: pillButton.name,
			},
		}).fire();

		setPill && setPill(pillButton.name);
	};

	return pills ? (
		<PillsContainer role="tablist">
			{pills.map((pillButton) => (
				<PillButton
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="select-task-view"
					key={pillButton.name}
					isSelected={pillButton.name === pill}
					onClick={() => onClick(pillButton)}
					role="tab"
					aria-selected={pillButton.name === pill}
				>
					{pillButton.i18n}
				</PillButton>
			))}
		</PillsContainer>
	) : null;
};
